import { useCallback, useState } from "react";
import { bekreftKode, sendKode } from "../state/session";

/**
 * Innlogging med engangskode. Ingen passord — se docs/02-arkitektur.md.
 *
 * Vises BARE naar Supabase er satt opp. Uten miljoevariabler finnes ikke denne
 * skjermen i det hele tatt, og appen kjoerer paa froeet.
 *
 * Den staar paa samme flate som oppstartsfeilen, med samme form: regi som ikke
 * kommer inn skal moete noe som ligner resten av appen, ikke et skjema.
 *
 * KODE, IKKE LENKE. Skjermen ba foer om at man aapnet en lenke, men malen
 * sender ingen — en lenke paa iOS aapner alltid i standardnettleseren, og en
 * PWA fra hjemskjermen har eget lager atskilt fra Safari. Oekten ville landet
 * i Safari og ikke i appen. Rettet 26.10.2026; foer det kom ingen inn.
 *
 * Dette er den eneste handlingen i appen som krever nett, og den skjer én
 * gang. Oekten lagres lokalt og overlever at appen lukkes.
 */
export function InnloggingScreen(): JSX.Element {
  const [epost, setEpost] = useState("");
  const [kode, setKode] = useState("");
  const [fase, setFase] = useState<"epost" | "sender" | "kode" | "bekrefter">("epost");
  const [feil, setFeil] = useState<string | null>(null);

  const send = useCallback(async (): Promise<void> => {
    if (epost.trim().length === 0 || fase === "sender") return;
    setFase("sender");
    setFeil(null);
    const svar = await sendKode(epost);
    if (svar.ok) { setFase("kode"); return; }
    setFase("epost");
    setFeil(svar.feil ?? "Koden kunne ikke sendes.");
  }, [epost, fase]);

  /* Lykkes den, fyrer `onAuthStateChange` i App.tsx og appen bytter flate
     selv. Derfor finnes det ingen «inne»-tilstand her. */
  const bekreft = useCallback(async (): Promise<void> => {
    if (kode.trim().length === 0 || fase === "bekrefter") return;
    setFase("bekrefter");
    setFeil(null);
    const svar = await bekreftKode(epost, kode);
    if (svar.ok) return;
    setFase("kode");
    setFeil("Koden stemmer ikke, eller den har gått ut. Be om en ny.");
  }, [epost, kode, fase]);

  if (fase === "kode" || fase === "bekrefter") {
    return (
      <div className="app">
        <div className="startup">
          <span className="startup__title">Skriv koden</span>
          <span className="startup__body">
            Finnes {epost.trim()} i en produksjon, ligger det en kode i
            innboksen nå.
          </span>

          <form
            className="innlogging__skjema"
            onSubmit={(e) => { e.preventDefault(); void bekreft(); }}
          >
            <input
              className="innlogging__felt innlogging__felt--kode mono"
              type="text"
              inputMode="numeric"
              /* Tastaturet og nøkkelringen kjenner igjen engangskoder på
                 dette ordet, og tilbyr koden fra varselet. */
              autoComplete="one-time-code"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="00000000"
              value={kode}
              onChange={(e) => setKode(e.target.value)}
              aria-label="Engangskode"
            />
            <button
              type="submit"
              className="innlogging__knapp"
              disabled={kode.trim().length === 0 || fase === "bekrefter"}
            >
              {fase === "bekrefter" ? "Sjekker …" : "Logg inn"}
            </button>
          </form>

          {feil === null ? null : <span className="startup__body">{feil}</span>}

          <button
            type="button"
            className="startup__bypass"
            onClick={() => { setKode(""); setFeil(null); setFase("epost"); }}
          >
            Skrev du feil adresse?
          </button>

          <span className="startup__meta">
            Koden virker én gang og går ut etter en time.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="startup">
        <span className="startup__title">Logg inn</span>
        <span className="startup__body">
          Skriv e-postadressen din, så sender vi en kode. Ingen passord.
        </span>

        <form
          className="innlogging__skjema"
          onSubmit={(e) => { e.preventDefault(); void send(); }}
        >
          <input
            className="innlogging__felt mono"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="deg@produksjonen.no"
            value={epost}
            onChange={(e) => setEpost(e.target.value)}
            aria-label="E-postadresse"
          />
          <button
            type="submit"
            className="innlogging__knapp"
            disabled={epost.trim().length === 0 || fase === "sender"}
          >
            {fase === "sender" ? "Sender …" : "Send kode"}
          </button>
        </form>

        {feil === null ? null : <span className="startup__body">{feil}</span>}

        <span className="startup__meta">
          Dette er det eneste som krever nett. Etterpå virker appen offline.
        </span>
      </div>
    </div>
  );
}
